import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID, WRAPPED_NATIVE_ADDRESSES } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useSolanaTokenSecurityInfo } from '@/hooks/useSolanaTokenSecurity';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import { TokenResult } from '@/utils/0x/token-registry.types';

import type { BadgeVariant } from '../components/TokenProfile/TokenProfileSecurityAudit/RiskIcons/BadgeIcon';
import {
  type SecurityAlertsResult,
  type SecurityLevel,
  calculateSecurityAlerts,
  calculateSolanaSecurityAlerts,
  getEvmSecurityScore,
  getSolanaSecurityLevel,
} from '../components/TokenProfile/TokenProfileSecurityAudit/utils';

interface Params {
  activeToken?: TokenResult;
}

interface SecurityAssessmentResult {
  level: SecurityLevel;
  risks: number;
  /** Always 0 on Solana (no high-risk tier). */
  highRisks: number;
  alerts: SecurityAlertsResult;
}

/**
 * Shared security-assessment data hook. Fetches CoinGecko listing info, then
 * branches between the EVM and Solana scorers. `getSecurityLabel` derives the
 * human-readable summary from the resulting level + risk count.
 */
export const useSecurityAssessment = ({ activeToken }: Params): SecurityAssessmentResult => {
  const { data: tokenInfo } = useTokenInfo(activeToken?.address, activeToken?.chainId);
  const { data: solanaSecurityData } = useSolanaTokenSecurityInfo({
    tokenAddress: activeToken?.chainId === CHAIN_IDS.SOLANA ? activeToken?.address : undefined,
  });

  const isListedOnCoinGecko = Boolean(tokenInfo?.info?.id);
  const isListedOnCoinbaseOrBinance =
    tokenInfo?.info?.tickers?.filter((ticker) => ticker.market?.name === 'Coinbase Exchange')[0]
      ?.trade_url !== undefined ||
    tokenInfo?.info?.tickers?.filter((ticker) => ticker.market?.name === 'Binance')[0]
      ?.trade_url !== undefined;

  if (!activeToken) {
    return {
      level: 'Pending',
      risks: 0,
      highRisks: 0,
      alerts: {
        externalDependencies: [],
        accessControls: [],
        issuanceInformation: [],
        customBehaviors: [],
        previewAlerts: [],
      },
    };
  }

  // Canonical chain assets (native + wrapped-native) short-circuit to Safe. These
  // are well-known low-risk assets where the underlying scoring data is either
  // missing (natives) or noisy (e.g. WETH/WBNB flagged as mintable). MAT-4301:
  // Mantle's native MNT is excluded because token-api overrides its values with
  // real risk signals.
  const isCanonicalChainAsset =
    activeToken.address === WRAPPED_NATIVE_ADDRESSES[activeToken.chainId] ||
    (activeToken.chainId !== CHAIN_IDS.MANTLE &&
      activeToken.address === NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[activeToken.chainId]);

  if (isCanonicalChainAsset) {
    return {
      level: 'Safe',
      risks: 0,
      highRisks: 0,
      alerts:
        activeToken.chainId === CHAIN_IDS.SOLANA
          ? calculateSolanaSecurityAlerts(solanaSecurityData)
          : calculateSecurityAlerts(activeToken),
    };
  }

  if (activeToken.chainId === CHAIN_IDS.SOLANA) {
    if (!solanaSecurityData) {
      return {
        level: 'Pending',
        risks: 0,
        highRisks: 0,
        alerts: calculateSolanaSecurityAlerts(undefined),
      };
    }
    const { level, risks } = getSolanaSecurityLevel(
      solanaSecurityData,
      isListedOnCoinbaseOrBinance,
      isListedOnCoinGecko,
    );
    return {
      level,
      risks,
      highRisks: 0,
      alerts: calculateSolanaSecurityAlerts(solanaSecurityData),
    };
  }

  const score = getEvmSecurityScore(activeToken, isListedOnCoinbaseOrBinance, isListedOnCoinGecko);
  return { ...score, alerts: calculateSecurityAlerts(activeToken) };
};

/** Short summary text shown on the score badge ("Pending", "Danger", or "N risk(s) detected"). */
export const getSecurityLabel = (level: SecurityLevel, riskCount: number): string => {
  if (level === 'Pending') return 'Pending';
  if (level === 'Danger') return 'Danger';
  return `${riskCount} ${riskCount === 1 ? 'risk' : 'risks'} detected`;
};

export const SECURITY_LEVEL_TO_BADGE_VARIANT: Record<SecurityLevel, BadgeVariant> = {
  Neutral: 'neutral',
  Risky: 'risky',
  Danger: 'danger',
  Pending: 'pending',
  Safe: 'safe',
};
